import { RaidChatHeader, RaidChatPinAction, RaidChatPinProvider } from './RaidChatHeader.jsx';
import { RaidChatHistoryControls } from './RaidChatHistoryControls.jsx';
import { useRaidChatHistoryScroll } from './useRaidChatHistoryScroll.js';
import { MAX_PRACTICE_SLOTS, PRACTICE_SLOT_NUMBERS } from '../../raid-practice/domain/raidPracticeSlots.js';
import { memo, useCallback, useEffect, useId, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { ChatSticker, ChatStickerPicker } from '../../stickers/ChatStickers.jsx';
import { DororaidIcon } from '../../../shared/ui/DororaidIcon.jsx';
import { splitChatMessageLinks } from '../domain/chatMessageLinks.js';
import { RaidPracticeDeckDisplay } from '../../raid-practice/shared/RaidPracticeDeckPicker.jsx';
import {
  extractRaidControlRepracticeRequest,
  RAID_CONTROL_REPRACTICE_REQUEST_STATUS,
  resolveRaidControlRepracticeRequestStatus,
} from '../../raid-control-repractice-planner/domain/raidControlRepracticeRequestCodec.js';
import { buildRaidControlNikkeAutocompleteSuggestions, resolveRaidControlNikkeAutocompleteQuery } from '../domain/raidControlChatAutocomplete.js';
import { raidControlTetrisModeLabel } from '../domain/raidControlTetrisShareCodec.js';
import { activateMobileChatComposerWithoutScroll, canOpenPlusMenu, invokeMobileChatPlusAction, targetPlusMenuSettleVersion } from './mobileChatInteractionState.js';
import '../../../styles/raid-control-mobile-chat-fresh.css';

function formatDamage(value) {
  if (value === null || value === undefined || value === '') return '-';
  return `${Number(value || 0).toLocaleString('ko-KR', { maximumFractionDigits: 3 })}억`;
}

// 포맷터를 한 번만 만든다. 만드는 쪽이 찍는 쪽보다 훨씬 비싸서, 메시지마다 새로 만들면
// 글 하나가 도착할 때마다 화면에 올라온 메시지 수만큼 포맷터가 생겼다.
let timeFormatter = null;

function timeLabel(value) {
  if (!value) return '';
  try {
    if (!timeFormatter) timeFormatter = new Intl.DateTimeFormat('ko-KR', { hour: '2-digit', minute: '2-digit' });
    return timeFormatter.format(new Date(value));
  } catch {
    return '';
  }
}

function nextAvailablePracticeSlot(records = []) {
  const used = new Set((Array.isArray(records) ? records : [])
    .map((record) => Number(record?.slotNo ?? record?.slot_no ?? 0))
    .filter((slotNo) => slotNo >= 1 && slotNo <= MAX_PRACTICE_SLOTS));
  for (let slotNo = 1; slotNo <= MAX_PRACTICE_SLOTS; slotNo += 1) if (!used.has(slotNo)) return slotNo;
  return null;
}

function messageSummary(message) {
  if (!message) return '원문을 찾을 수 없습니다.';
  const repracticeRequest = extractRaidControlRepracticeRequest(message.nikkeRefs);
  if (repracticeRequest) return `${repracticeRequest.memberNickname} 모의전 요청 · 필요 실측 ${formatDamage(repracticeRequest.requiredMeasuredDamageEok)} 이상`;
  if (message.type === 'raid_result') return `${message.eventData?.memberNickname || message.authorNickname} 실전 ${formatDamage(message.eventData?.damageEok)}`;
  if (message.type === 'practice_report') return `${message.eventData?.memberNickname || message.authorNickname} 모의전 ${formatDamage(message.eventData?.damageEok)}`;
  if (message.type === 'tetris_share' && message.tetrisShare) {
    if (message.tetrisShare.scope === 'global') return `테트리스 전체 ${Math.max(1, Number(message.tetrisShare.planCount || 1))}보스 계획`;
    return `${message.tetrisShare.bossName || '보스'} 테트리스 ${formatDamage(message.tetrisShare.totalEok)}`;
  }
  return String(message.body || '').trim().slice(0, 90) || '메시지';
}

function resolveMentionEntries(body, members = [], allowAll = false) {
  // @ 가 없는 글이 대부분이다. 아래 멤버 표를 먼저 만들면 그 대부분이 멤버 수만큼 헛일을 한다.
  const tokens = String(body || '').match(/@[^\s@]+/g);
  if (!tokens) return [];
  const mentionByToken = new Map(members
    .filter((member) => member.userId && member.nickname)
    .map((member) => [`@${member.nickname}`, {
      token: `@${member.nickname}`,
      userId: member.userId,
      nickname: member.nickname,
    }]));
  if (allowAll) mentionByToken.set('@전체', { token: '@전체', userId: '__all__', nickname: '전체' });
  const seen = new Set();
  return tokens
    .map((token) => mentionByToken.get(token))
    .filter((entry) => entry && !seen.has(entry.token) && seen.add(entry.token));
}

function escapeRegExp(value) {
  return String(value || '').replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

function chatMessageVisible(message) {
  if (message?.type === 'raid_result') return false;
  const share = message?.type === 'tetris_share' ? message.tetrisShare : null;
  if (!share || share.scope !== 'global') return true;
  const planIndex = Number(share.planIndex);
  return !Number.isFinite(planIndex) || planIndex <= 0;
}

// 링크를 먼저 떼어낸 다음 남은 글자에서 멘션을 찾는다. 순서를 바꾸면 주소 안의 @이름이 멘션으로
// 잘려 나가 링크가 끊긴다(https://x.com/@nikke 같은 주소).
function MentionText({ body, members, userId }) {
  const mentionByToken = new Map(resolveMentionEntries(body, members, true).map((entry) => [entry.token, entry]));
  return splitChatMessageLinks(body).map((token, index) => {
    if (token.type === 'link') {
      return <a
        className="doro-mobile-chat__link"
        key={`link-${index}`}
        href={token.href}
        target="_blank"
        rel="noreferrer noopener"
      >{token.value}</a>;
    }
    return token.value.split(/(@[^\s@]+)/g).map((part, partIndex) => {
      const mention = mentionByToken.get(part);
      if (!mention) return part;
      const className = [
        'doro-mobile-chat__mention',
        mention.userId === userId ? 'is-me' : '',
        mention.userId === '__all__' ? 'is-all' : '',
      ].filter(Boolean).join(' ');
      return <mark className={className} key={`${index}-${part}-${partIndex}`}>{part}</mark>;
    });
  });
}

function ReplyPreview({ replyMessage, onJump }) {
  if (!replyMessage) return null;
  return <button type="button" className="doro-mobile-chat__reply-preview" onClick={() => onJump(replyMessage.id)}>
    <strong>{replyMessage.authorNickname}</strong>
    <span>{messageSummary(replyMessage)}</span>
  </button>;
}

function ReplyAction({ message, onReply }) {
  return <span className="raid-control-message-actions"><button type="button" className="doro-mobile-chat__reply-action" onClick={() => onReply(message)}>답장</button><RaidChatPinAction messageId={message.id} /></span>;
}

function NikkeRefs({ items = [] }) {
  if (!items.length) return null;
  return <div className="doro-mobile-chat__nikkes">{items.map((nikke) => <span key={nikke.nikkeId || nikke.name}>
    {nikke.imageUrl ? <img src={siteImageUrl(nikke.imageUrl)} alt="" /> : null}
    {nikke.name}
  </span>)}</div>;
}

function tetrisNotice(share) {
  if (share?.scope === 'global') return `전체 ${Math.max(1, Number(share.planCount || 1))}보스 계획이 테트리스 결과에 등록됐습니다.`;
  const diff = Number(share?.diffEok || 0);
  return `${share?.bossName || '보스'} · ${share?.phaseNo || '-'}단 · ${share?.items?.length || 0}타 · 차이 ${diff >= 0 ? '+' : '-'}${formatDamage(Math.abs(diff))}`;
}

function RepracticeRequestEvent({ message, request, room, userId, replyMessage, onReply, onJump }) {
  const [resultOpen, setResultOpen] = useState(false);
  const [damageEok, setDamageEok] = useState('');
  const [saveTarget, setSaveTarget] = useState('');
  const [saving, setSaving] = useState(false);
  const [localError, setLocalError] = useState('');
  const [savedNotice, setSavedNotice] = useState('');
  const boss = room.bosses.find((item) => Number(item.bossNo) === Number(request.bossNo)) || {};
  const practiceRecords = Array.isArray(room.context?.practiceRecords) ? room.context.practiceRecords : [];
  const actualRecords = Array.isArray(room.overview?.selectedSeason?.records) ? room.overview.selectedSeason.records : [];
  const lockedOrders = room.ordersById instanceof Map ? [...room.ordersById.values()] : [];
  const targetMember = room.members.find((member) => String(member.memberOpenid || '') === String(request.memberOpenid || '')) || null;
  const isTarget = Boolean(
    (request.targetUserId && request.targetUserId === userId)
    || (targetMember?.userId && targetMember.userId === userId),
  );
  const memberPractices = useMemo(() => practiceRecords
    .filter((record) => String(record.memberOpenid || '') === String(request.memberOpenid || '') && Number(record.bossNo) === Number(request.bossNo))
    .sort((left, right) => Number(left.slotNo) - Number(right.slotNo)), [practiceRecords, request.bossNo, request.memberOpenid]);
  const basePractice = memberPractices.find((record) => String(record.id || '') === String(request.basePracticeId || '')) || null;
  const freeSlot = nextAvailablePracticeSlot(memberPractices);
  const defaultSaveTarget = freeSlot ? `new:${freeSlot}` : basePractice ? `replace:${basePractice.id}` : '';
  const result = useMemo(() => resolveRaidControlRepracticeRequestStatus(request, {
    messageCreatedAt: message.createdAt,
    seasonNo: room.context?.season?.seasonNo || room.overview?.selectedSeason?.seasonNo || 0,
    boss,
    practiceRecords,
    actualRecords,
    lockedOrders,
  }), [actualRecords, boss, lockedOrders, message.createdAt, practiceRecords, request, room.context?.season?.seasonNo, room.overview?.selectedSeason?.seasonNo]);
  const statusLabel = result.status === RAID_CONTROL_REPRACTICE_REQUEST_STATUS.COMPLETED
    ? '컷 통과'
    : result.status === RAID_CONTROL_REPRACTICE_REQUEST_STATUS.BELOW_CUTOFF
      ? '컷 미달'
      : result.status === RAID_CONTROL_REPRACTICE_REQUEST_STATUS.EXPIRED ? '만료' : '실측 대기';
  const canRegister = isTarget && [
    RAID_CONTROL_REPRACTICE_REQUEST_STATUS.PENDING,
    RAID_CONTROL_REPRACTICE_REQUEST_STATUS.BELOW_CUTOFF,
  ].includes(result.status);

  useEffect(() => {
    setResultOpen(false);
    setDamageEok('');
    setSaveTarget(defaultSaveTarget);
    setLocalError('');
    setSavedNotice('');
  }, [defaultSaveTarget, request.planRevision]);

  const submitResult = async (event) => {
    event.preventDefault();
    if (!canRegister || saving || room.working) return;
    const measured = Number(damageEok);
    if (!Number.isFinite(measured) || measured <= 0) {
      setLocalError('실제 모의전 피해량을 0보다 크게 입력해주세요.');
      return;
    }
    if (!Array.isArray(request.variantDeck) || request.variantDeck.length !== 5) {
      setLocalError('요청 덱 정보를 확인할 수 없습니다.');
      return;
    }
    if (!saveTarget) {
      setLocalError('저장 가능한 슬롯이 없습니다.');
      return;
    }

    const replaceBase = saveTarget.startsWith('replace:');
    const slotNo = replaceBase ? Number(basePractice?.slotNo || 0) : Number(saveTarget.split(':')[1] || 0);
    if (!slotNo) {
      setLocalError('저장할 슬롯을 확인해주세요.');
      return;
    }
    const title = `재모의전 ${request.removeNikkeName || '기존'}→${request.addNikkeName || '변경'}`.slice(0, 100);
    const payload = {
      slotNo,
      title,
      damageEok: measured,
      used: false,
      deck: [...request.variantDeck],
    };

    setSaving(true);
    setLocalError('');
    setSavedNotice('');
    try {
      const saved = replaceBase
        ? await room.updatePractice({ record: basePractice, targetMemberOpenid: request.memberOpenid, bossNo: request.bossNo, payload })
        : await room.createPractice({ targetMemberOpenid: request.memberOpenid, bossNo: request.bossNo, payload });
      if (!saved) {
        setLocalError(room.practiceNotice?.message || '모의전 실측값을 저장하지 못했습니다.');
        return;
      }

      const passed = measured >= Number(request.requiredMeasuredDamageEok || 0);
      const shared = await room.sendPracticeReport({
        targetMemberOpenid: request.memberOpenid,
        bossNo: request.bossNo,
        phaseNo: request.phaseNo || boss.currentPhase || 1,
        damageEok: measured,
        body: `재모의전 결과 · 필요 실측 ${formatDamage(request.requiredMeasuredDamageEok)} · ${passed ? '컷 통과' : '컷 미달'}`,
        nikkeRefs: request.variantDeck.map((nikkeId) => ({ nikkeId })),
      });
      await room.refresh({ silent: true });
      setResultOpen(false);
      setSavedNotice(`실측 ${formatDamage(measured)} 저장 · ${passed ? '컷 통과' : '컷 미달'}${shared ? '' : ' · 채팅 공유 실패'}`);
    } catch (error) {
      setLocalError(error instanceof Error ? error.message : '모의전 실측값을 저장하지 못했습니다.');
    } finally {
      setSaving(false);
    }
  };

  const anchorProps = { id: `doro-mobile-chat-message-${message.id}`, 'data-message-id': message.id };
  return <article {...anchorProps} className="doro-mobile-chat__event">
    <ReplyPreview replyMessage={replyMessage} onJump={onJump} />
    <header><div><span>RE-PRACTICE REQUEST</span><strong>{request.memberNickname} · {request.bossName} {request.phaseNo}단</strong></div><em>{statusLabel}</em></header>
    <p>{request.removeNikkeName || '기존'} → {request.addNikkeName || '변경'}</p>
    <div className="doro-mobile-chat__event-result"><strong>현재 {formatDamage(request.basePracticeDamageEok)}</strong><b>필요 {formatDamage(request.requiredMeasuredDamageEok)} 이상</b></div>
    {request.variantDeck?.length === 5 ? <RaidPracticeDeckDisplay deck={request.variantDeck} mobile /> : null}
    {result.measuredDamageEok !== null ? <div className="doro-mobile-chat__event-result"><strong>실측 결과</strong><b>{formatDamage(result.measuredDamageEok)}</b></div> : null}
    {result.status === RAID_CONTROL_REPRACTICE_REQUEST_STATUS.EXPIRED ? <p>이 요청은 현재 계획에서 만료되었습니다.</p> : null}
    {result.status === RAID_CONTROL_REPRACTICE_REQUEST_STATUS.COMPLETED ? <p>등록된 실측값이 필요 실측 컷을 충족했습니다.</p> : null}
    {result.status === RAID_CONTROL_REPRACTICE_REQUEST_STATUS.BELOW_CUTOFF ? <p>실측값은 저장됐지만 현재 필요 컷에는 미달합니다. 이 값으로 계획이 다시 계산됩니다.</p> : null}
    {result.status === RAID_CONTROL_REPRACTICE_REQUEST_STATUS.PENDING ? <p>{isTarget ? '모의전 후 실제 측정값을 등록해주세요.' : '대상 멤버의 실측 결과를 기다리는 요청입니다.'}</p> : null}
    {savedNotice ? <p role="status">{savedNotice}</p> : null}
    {canRegister ? <button type="button" onClick={() => { setResultOpen((value) => !value); setLocalError(''); }} disabled={saving || Boolean(room.working)}>
      {result.status === RAID_CONTROL_REPRACTICE_REQUEST_STATUS.BELOW_CUTOFF ? '다시 결과 등록' : '모의전 결과 등록'}
    </button> : null}
    {resultOpen && canRegister ? <form className="doro-mobile-chat__repractice-result" onSubmit={submitResult}>
      <label><span>실제 피해량(억)</span><input type="number" min="0.001" step="0.001" inputMode="decimal" value={damageEok} onChange={(event) => { setDamageEok(event.target.value); setLocalError(''); }} placeholder="예: 302.4" /></label>
      <label><span>저장 슬롯</span><select value={saveTarget} onChange={(event) => setSaveTarget(event.target.value)}>
        {PRACTICE_SLOT_NUMBERS.filter((slotNo) => !memberPractices.some((record) => Number(record.slotNo) === slotNo)).map((slotNo) => <option key={`new-${slotNo}`} value={`new:${slotNo}`}>SLOT {slotNo}에 새로 저장</option>)}
        {basePractice ? <option value={`replace:${basePractice.id}`}>기준 SLOT {basePractice.slotNo} 덮어쓰기</option> : null}
        {!defaultSaveTarget ? <option value="">저장 가능한 슬롯 없음</option> : null}
      </select></label>
      <p>요청된 5인 덱을 그대로 저장합니다. 예상값이 아니라 입력한 실제 모의전 수치만 반영됩니다.</p>
      {localError ? <p className="doro-mobile-chat__error" role="alert">{localError}</p> : null}
      <div><button type="button" onClick={() => setResultOpen(false)} disabled={saving}>취소</button><button type="submit" disabled={saving || !saveTarget || !damageEok}>{saving ? '저장 중…' : '실측 저장'}</button></div>
    </form> : null}
    <small>{message.authorNickname} 요청 · {timeLabel(message.createdAt)}</small>
    <ReplyAction message={message} onReply={onReply} />
  </article>;
}

function EventMessage({ message, room, userId, replyMessage, onReply, onJump }) {
  const nikkeRefs = Array.isArray(message.nikkeRefs) ? message.nikkeRefs : [];
  const anchorProps = { id: `doro-mobile-chat-message-${message.id}`, 'data-message-id': message.id };
  const repracticeRequest = extractRaidControlRepracticeRequest(nikkeRefs);

  if (repracticeRequest) {
    return <RepracticeRequestEvent message={message} request={repracticeRequest} room={room} userId={userId} replyMessage={replyMessage} onReply={onReply} onJump={onJump} />;
  }

  if (message.type === 'tetris_share' && message.tetrisShare) {
    const share = message.tetrisShare;
    const title = share.scope === 'global'
      ? `전체 ${Math.max(1, Number(share.planCount || 1))}보스 계획`
      : `${share.bossName || '보스'} · ${share.phaseNo || '-'}단`;
    return <article {...anchorProps} className="doro-mobile-chat__event">
      <ReplyPreview replyMessage={replyMessage} onJump={onJump} />
      <header><div><span>TETRIS · {raidControlTetrisModeLabel(share.mode)}</span><strong>{title}</strong></div><em>공유</em></header>
      <p>{tetrisNotice(share)}</p>
      <small>{message.authorNickname} · {timeLabel(message.createdAt)}</small>
      <ReplyAction message={message} onReply={onReply} />
    </article>;
  }

  if (message.type === 'raid_result') {
    const event = message.eventData || {};
    return <article {...anchorProps} className="doro-mobile-chat__event">
      <ReplyPreview replyMessage={replyMessage} onJump={onJump} />
      <header><div><span>실전 딜 반영</span><strong>{event.bossName || '보스'} · {event.phaseNo || '-'}단</strong></div>{event.isKill ? <em>처치</em> : null}</header>
      <div className="doro-mobile-chat__event-result"><strong>{event.memberNickname || message.authorNickname}</strong><b>{formatDamage(event.damageEok)}</b></div>
      {event.deck?.length ? <RaidPracticeDeckDisplay deck={event.deck} mobile /> : null}
      <small>{timeLabel(event.attackedAt || message.createdAt)} 반영</small>
      <ReplyAction message={message} onReply={onReply} />
    </article>;
  }

  if (message.type === 'practice_report') {
    const event = message.eventData || {};
    return <article {...anchorProps} className="doro-mobile-chat__event">
      <ReplyPreview replyMessage={replyMessage} onJump={onJump} />
      <header><div><span>모의전 수동 입력</span><strong>{event.bossName || '보스'} · {event.phaseNo || '-'}단</strong></div><em>공유</em></header>
      <div className="doro-mobile-chat__event-result"><strong>{event.memberNickname || message.authorNickname}</strong><b>{formatDamage(event.damageEok)}</b></div>
      <NikkeRefs items={nikkeRefs} />
      {message.body && message.body !== '모의전 수동 입력' ? <p>{message.body}</p> : null}
      <small>{message.authorNickname} 입력 · {timeLabel(message.createdAt)}</small>
      <ReplyAction message={message} onReply={onReply} />
    </article>;
  }

  return null;
}

const EVENT_MESSAGE_TYPES = new Set(['tetris_share', 'raid_result', 'practice_report']);

function isEventMessage(message) {
  return EVENT_MESSAGE_TYPES.has(message.type) || Boolean(extractRaidControlRepracticeRequest(message.nikkeRefs));
}

// 평범한 말풍선은 아래 값이 그대로면 다시 그릴 것이 없다. room 을 통째로 받으면 방 상태가 조금
// 달라져도(보내는 중 표시, 주문 갱신 등) 신원이 바뀌어 memo 가 통하지 않으므로 쓰는 값만 끊어 받는다.
// 글쓴이 찾기도 부모가 한 번 만든 표에서 넘겨받는다. 메시지마다 멤버 목록을 훑던 자리다.
const TextMessage = memo(function TextMessage({ message, members, authorMember, userId, replyMessage, onReply, onJump }) {
  const mine = message.authorUserId === userId;
  const avatarUrl = String(authorMember?.avatarUrl || '').trim();
  const nikkeRefs = Array.isArray(message.nikkeRefs) ? message.nikkeRefs : [];

  return <article
    id={`doro-mobile-chat-message-${message.id}`}
    data-message-id={message.id}
    className={`doro-mobile-chat__message${mine ? ' mine' : ''}`}
  >
    {!mine ? <div className="doro-mobile-chat__avatar">{avatarUrl ? <img src={avatarUrl} alt="" /> : message.authorNickname.slice(0, 1)}</div> : null}
    <div className="doro-mobile-chat__message-main">
      <div className="doro-mobile-chat__meta">
        {!mine ? <strong>{message.authorNickname}</strong> : null}
        <time>{timeLabel(message.createdAt)}</time>
        <ReplyAction message={message} onReply={onReply} />
      </div>
      <ReplyPreview replyMessage={replyMessage} onJump={onJump} />
      <div className="doro-mobile-chat__bubble">{message.type === 'sticker' ? <ChatSticker stickerId={message.stickerId} stickerIds={message.stickerIds} /> : <MentionText body={message.body} members={members} userId={userId} />}</div>
      <NikkeRefs items={nikkeRefs} />
    </div>
  </article>;
});

function MessageComposer({ room, userId, replyTo, onCancelReply, onSelfRequestOpen, onOrderOpen, onFocusStart, keyboardOpen = false, viewportSettled = false, settleVersion = 0 }) {
  const [body, setBody] = useState('');
  const [nikkeRefs, setNikkeRefs] = useState([]);
  const [commandError, setCommandError] = useState('');
  const [sendFailed, setSendFailed] = useState(false);
  const [plusOpen, setPlusOpen] = useState(false);
  const [plusOpenTargetVersion, setPlusOpenTargetVersion] = useState(null);
  const plusPointerStartedWithTextareaFocusRef = useRef(false);
  const textareaRef = useRef(null);
  const textareaId = useId();
  const selfMember = useMemo(() => room.members.find((member) => member.userId === userId) || null, [room.members, userId]);
  const activeMentions = useMemo(() => resolveMentionEntries(body, room.members, room.canDirect), [body, room.canDirect, room.members]);
  const mentionMatch = body.match(/(?:^|\s)@([^\s@]*)$/);
  const mentionActive = Boolean(mentionMatch);
  const mentionQuery = String(mentionMatch?.[1] || '').toLocaleLowerCase('ko-KR');
  const nikkeQuery = useMemo(() => resolveRaidControlNikkeAutocompleteQuery(body), [body]);
  const mentionSuggestions = useMemo(() => {
    if (!mentionActive) return [];
    const members = room.members
      .filter((member) => member.userId && member.nickname.toLocaleLowerCase('ko-KR').includes(mentionQuery))
      .slice(0, 6);
    if (room.canDirect && '전체'.includes(mentionQuery)) {
      return [{ userId: '__all__', nickname: '전체', online: true }, ...members].slice(0, 6);
    }
    return members;
  }, [mentionActive, mentionQuery, room.canDirect, room.members]);
  const nikkeSuggestions = useMemo(() => !mentionActive
    ? buildRaidControlNikkeAutocompleteSuggestions(room.catalog.items, nikkeQuery)
    : [], [nikkeQuery, mentionActive, room.catalog.items]);

  useEffect(() => {
    if (plusOpenTargetVersion === null) return;
    if (!canOpenPlusMenu({ viewportSettled, settleVersion, targetSettleVersion: plusOpenTargetVersion })) return;
    setPlusOpenTargetVersion(null);
    setPlusOpen(true);
  }, [plusOpenTargetVersion, settleVersion, viewportSettled]);

  useEffect(() => {
    if (plusOpen && !viewportSettled) setPlusOpen(false);
  }, [plusOpen, viewportSettled]);

  const keepFocus = (event) => {
    if (event.cancelable) event.preventDefault();
  };

  const refocus = () => window.requestAnimationFrame(() => textareaRef.current?.focus({ preventScroll: true }));

  const togglePlusMenu = () => {
    if (plusOpen) {
      setPlusOpen(false);
      setPlusOpenTargetVersion(null);
      return;
    }
    const requiresCloseCycle = plusPointerStartedWithTextareaFocusRef.current || keyboardOpen || !viewportSettled;
    plusPointerStartedWithTextareaFocusRef.current = false;
    setPlusOpen(false);
    setPlusOpenTargetVersion(targetPlusMenuSettleVersion(settleVersion, requiresCloseCycle));
    textareaRef.current?.blur();
  };

  const runPlusAction = (action) => {
    setPlusOpen(false);
    setPlusOpenTargetVersion(null);
    invokeMobileChatPlusAction(action, { onSelfRequestOpen, onOrderOpen });
  };

  const selectNikke = (item) => {
    setBody((value) => value.replace(/\S+$/, '').replace(/[ \t]+$/, (spaces) => spaces ? ' ' : ''));
    setNikkeRefs((current) => current.some((entry) => entry.nikkeId === item.nikkeId)
      ? current
      : [...current, { nikkeId: item.nikkeId, name: item.name, imageUrl: item.imageUrl }].slice(0, 5));
    refocus();
  };

  const selectMention = (member) => {
    setBody((value) => {
      const match = value.match(/(?:^|\s)@[^\s@]*$/);
      if (!match) return `${value}${value && !/\s$/.test(value) ? ' ' : ''}@${member.nickname} `;
      const leading = match[0].match(/^\s/)?.[0] || '';
      return `${value.slice(0, match.index)}${leading}@${member.nickname} `;
    });
    refocus();
  };

  const removeMention = (mention) => {
    const expression = new RegExp(`(^|\\s)${escapeRegExp(mention.token)}(?=\\s|$)`, 'g');
    setBody((value) => value.replace(expression, '$1').replace(/[ \t]{2,}/g, ' ').trimStart());
    refocus();
  };

  const submit = async (event) => {
    event.preventDefault();
    const clean = body.trim();
    if (!clean) return;
    setSendFailed(false);

    if (clean.startsWith('/모의전')) {
      const match = clean.match(/^\/모의전\s+([0-9]+(?:\.[0-9]+)?)\s*(.*)$/);
      const boss = room.bosses.find((item) => item.bossNo === room.selectedBossNo);
      if (!match || !selfMember || !boss) {
        setCommandError('본인 멤버 정보와 보스를 확인한 뒤 /모의전 12.3 메모 형식으로 입력해주세요.');
        return;
      }
      const damage = Number(match[1]);
      if (!Number.isFinite(damage) || damage <= 0) {
        setCommandError('모의전 피해량은 0보다 큰 억 단위 숫자로 입력해주세요.');
        return;
      }
      const ok = await room.sendPracticeReport({
        targetMemberOpenid: selfMember.memberOpenid,
        bossNo: boss.bossNo,
        phaseNo: boss.currentPhase,
        damageEok: damage,
        body: match[2],
        nikkeRefs,
      });
      if (ok) {
        setBody('');
        setNikkeRefs([]);
        setCommandError('');
        onCancelReply();
      } else {
        setSendFailed(true);
      }
      return;
    }

    const mentionedUserIds = new Set(activeMentions
      .filter((mention) => mention.userId !== '__all__')
      .map((mention) => mention.userId));
    if (activeMentions.some((mention) => mention.userId === '__all__')) {
      room.members.forEach((member) => member.userId && mentionedUserIds.add(member.userId));
    }
    const ok = await room.sendMessage({
      body: clean,
      mentionedUserIds: [...mentionedUserIds],
      nikkeRefs,
      replyToId: replyTo?.id || null,
    });
    if (ok) {
      setBody('');
      setNikkeRefs([]);
      setCommandError('');
      setSendFailed(false);
      onCancelReply();
    } else {
      setSendFailed(true);
    }
  };

  // 재연결·인증 대기와 정상 연결이 구분되지 않아, 입력은 되는데 전송만 안 되는 것처럼
  // 보였다. 끊겨 있을 때만 한 줄로 알린다.
  const offline = room.realtimeStatus && room.realtimeStatus !== 'open';

  return <form className="doro-mobile-chat__composer" onSubmit={submit}>
    {offline ? <p className="doro-mobile-chat__connection" role="status">
      {room.realtimeStatus === 'unavailable' ? '채팅 서버에 연결할 수 없습니다. 잠시 후 다시 시도해주세요.' : '연결 중… 연결되면 바로 전송됩니다.'}
    </p> : null}
    {replyTo ? <div className="doro-mobile-chat__replying">
      <span><small>{replyTo.authorNickname}에게 답장</small><strong>{messageSummary(replyTo)}</strong></span>
      <button type="button" onClick={onCancelReply} aria-label="답장 취소"><DororaidIcon name="close" size={16} /></button>
    </div> : null}

    {mentionSuggestions.length ? <div className="doro-mobile-chat__suggestions" aria-label="멤버 멘션 선택">
      {mentionSuggestions.map((member) => <button key={member.userId} type="button" onPointerDown={keepFocus} onClick={() => selectMention(member)}>
        {member.avatarUrl ? <img src={member.avatarUrl} alt="" /> : null}
        <span><strong>@{member.nickname}</strong><small>{member.userId === '__all__' ? '운영진 전체 멘션' : member.online ? '접속 중' : '멤버 멘션'}</small></span>
      </button>)}
    </div> : null}

    {!mentionSuggestions.length && nikkeSuggestions.length ? <div className="doro-mobile-chat__suggestions">
      {nikkeSuggestions.map((item) => <button key={item.nikkeId} type="button" onPointerDown={keepFocus} onClick={() => selectNikke(item)}>
        {item.imageUrl ? <img src={siteImageUrl(item.imageUrl)} alt="" /> : null}
        <span><strong>{item.name}</strong><small>{(item.aliases || []).join(' · ') || item.element}</small></span>
      </button>)}
    </div> : null}

    {activeMentions.length ? <div className="doro-mobile-chat__mentions" aria-label="적용된 멘션">
      <small>멘션 적용</small>
      {activeMentions.map((mention) => <button type="button" key={mention.token} onPointerDown={keepFocus} onClick={() => removeMention(mention)}>{mention.token} ×</button>)}
    </div> : null}

    {nikkeRefs.length ? <div className="doro-mobile-chat__chips">
      {nikkeRefs.map((item) => <button type="button" key={item.nikkeId} onPointerDown={keepFocus} onClick={() => setNikkeRefs((current) => current.filter((entry) => entry.nikkeId !== item.nikkeId))}>{item.name} ×</button>)}
    </div> : null}

    {commandError ? <p className="doro-mobile-chat__error">{commandError}</p> : null}
    {sendFailed ? <p className="doro-mobile-chat__error">전송에 실패했습니다. 입력 내용은 유지했습니다.</p> : null}

    {plusOpen && viewportSettled ? <div className="doro-mobile-chat__menu" role="menu">
      <button role="menuitem" type="button" onClick={() => runPlusAction('self-request')}><span><strong>내 공격덱 등록</strong><small>내 공격 덱을 등록하고 승인 요청</small></span></button>
      {room.canDirect ? <button role="menuitem" type="button" onClick={() => runPlusAction('order')}><span><strong>공격 지시</strong><small>새 덱을 직접 작성해 공격 지시</small></span></button> : null}
    </div> : null}

    <div className="doro-mobile-chat__composer-row">
      <button
        type="button"
        className="doro-mobile-chat__plus"
        aria-label="관제 기능 열기"
        aria-expanded={plusOpen && viewportSettled}
        aria-busy={plusOpenTargetVersion !== null || undefined}
        onPointerDown={() => { plusPointerStartedWithTextareaFocusRef.current = document.activeElement === textareaRef.current; }}
        onClick={togglePlusMenu}
      >+</button>
      <div className="doro-mobile-chat__input-shell">
        <textarea
          id={textareaId}
          ref={textareaRef}
          data-focus-scroll-owner="release-click-prevent-scroll"
          aria-label="메시지 입력"
          value={body}
          maxLength={500}
          rows={1}
          placeholder="메시지 입력"
          onFocus={onFocusStart}
          onChange={(event) => {
            setBody(event.target.value);
            setCommandError('');
            setSendFailed(false);
          }}
          onKeyDown={(event) => {
            if (event.key === 'Enter' && !event.shiftKey && !event.isComposing) {
              event.preventDefault();
              event.currentTarget.form?.requestSubmit();
            }
          }}
        />
        <label
          className="doro-mobile-chat__input-touch-guard"
          htmlFor={textareaId}
          aria-hidden="true"
          onClick={(event) => activateMobileChatComposerWithoutScroll(event, document.activeElement, textareaRef.current)}
          onContextMenu={keepFocus}
        />
      </div>
      <ChatStickerPicker room={room} onOpen={() => { setPlusOpen(false); setPlusOpenTargetVersion(null); }} />
      <button type="submit" className="doro-mobile-chat__send" onPointerDown={keepFocus} disabled={!body.trim() || ['message', 'practice'].includes(room.working)} aria-label="전송">
        <DororaidIcon name="chevronRight" size={19} />
      </button>
    </div>
  </form>;
}

export function MobileRaidControlChat({ noticeExpanded = false, room, userId, onSelfRequestOpen, onOrderOpen, keyboardOpen = false, keyboardPhase = 'closed', viewportSettled = false, settleVersion = 0 }) {
  const rootRef = useRef(null);
  const scrollRef = useRef(null);
  const { loadOlder, onHistoryScroll } = useRaidChatHistoryScroll(scrollRef, room);
  const lastMessageIdRef = useRef('');
  const wasHistoryRef = useRef(false);
  const lastMarkedAtRef = useRef('');
  const initialSeasonRef = useRef('');
  const keyboardFollowBottomRef = useRef(true);
  const previousKeyboardPhaseRef = useRef('closed');
  const [replyTo, setReplyTo] = useState(null);
  const [newMessageCount, setNewMessageCount] = useState(0);
  const chatMessages = useMemo(() => room.messages.filter(chatMessageVisible), [room.messages]);
  const messageById = useMemo(() => new Map(chatMessages.map((message) => [message.id, message])), [chatMessages]);
  const memberIndex = useMemo(() => {
    const byUserId = new Map();
    const byNickname = new Map();
    room.members.forEach((member) => {
      if (member.userId && !byUserId.has(member.userId)) byUserId.set(member.userId, member);
      if (member.nickname && !byNickname.has(member.nickname)) byNickname.set(member.nickname, member);
    });
    return { byUserId, byNickname };
  }, [room.members]);
  // 종류 가리기와 글쓴이 찾기는 메시지 목록이 바뀔 때만 하면 된다. 렌더마다 하면 글 하나가
  // 도착할 때 화면에 올라온 메시지 전부를 다시 훑는다.
  const chatEntries = useMemo(() => chatMessages.map((message) => ({
    message,
    event: isEventMessage(message),
    authorMember: memberIndex.byUserId.get(message.authorUserId)
      || memberIndex.byNickname.get(message.authorNickname)
      || null,
  })), [chatMessages, memberIndex]);
  const unread = useMemo(() => {
    if (!room.unreadAnchorId) return { anchorId: '', count: 0 };
    const startIndex = room.messages.findIndex((message) => message.id === room.unreadAnchorId);
    if (startIndex < 0) return { anchorId: '', count: 0 };
    const visibleUnread = room.messages.slice(startIndex)
      .filter((message) => chatMessageVisible(message) && message.type !== 'attack_order' && message.authorUserId !== userId);
    return { anchorId: visibleUnread[0]?.id || '', count: visibleUnread.length };
  }, [room.messages, room.unreadAnchorId, userId]);

  const nearBottom = (node) => node.scrollHeight - node.scrollTop - node.clientHeight < 72;

  const captureKeyboardFollow = () => {
    keyboardFollowBottomRef.current = Boolean(scrollRef.current && nearBottom(scrollRef.current));
  };

  useLayoutEffect(() => {
    if (previousKeyboardPhaseRef.current === keyboardPhase) return;
    previousKeyboardPhaseRef.current = keyboardPhase;
    if (!keyboardFollowBottomRef.current) return;
    const node = scrollRef.current;
    if (node) node.scrollTop = node.scrollHeight;
  }, [keyboardPhase]);

  const markLatestRead = () => {
    const latestAt = room.messages[room.messages.length - 1]?.createdAt || '';
    if (!latestAt || lastMarkedAtRef.current === latestAt) return;
    lastMarkedAtRef.current = latestAt;
    room.markReadThrough(latestAt);
  };

  const scrollToLatest = (behavior = 'smooth') => {
    if (room.historyMode) { room.showLatestMessages(); return; }
    const node = scrollRef.current;
    if (!node) return;
    node.scrollTo({ top: node.scrollHeight, behavior });
    setNewMessageCount(0);
    window.requestAnimationFrame(markLatestRead);
  };

  // ref 와 DOM 만 쓴다. 매 렌더마다 새 함수를 넘기면 말풍선 memo 가 전부 깨진다.
  const jumpToMessage = useCallback((messageId) => {
    const list = scrollRef.current;
    const target = list?.querySelector(`[data-message-id="${messageId}"]`);
    if (!list || !target) return;
    const listRect = list.getBoundingClientRect();
    const targetRect = target.getBoundingClientRect();
    const nextTop = list.scrollTop + targetRect.top - listRect.top - ((list.clientHeight - targetRect.height) / 2);
    list.scrollTo({ top: Math.max(nextTop, 0), behavior: 'smooth' });
    target.classList.add('doro-mobile-chat__flash');
    window.setTimeout(() => target.classList.remove('doro-mobile-chat__flash'), 1100);
  }, []);

  useLayoutEffect(() => {
    const seasonKey = String(room.context.season?.id || room.context.season?.seasonNo || '');
    if (!seasonKey || !chatMessages.length || initialSeasonRef.current === seasonKey) return;
    initialSeasonRef.current = seasonKey;
    lastMessageIdRef.current = chatMessages[chatMessages.length - 1]?.id || '';
    const list = scrollRef.current;
    if (list) list.scrollTop = list.scrollHeight;
    setNewMessageCount(0);
    markLatestRead();
  }, [chatMessages, room.context.season?.id, room.context.season?.seasonNo]);

  useEffect(() => {
    if (wasHistoryRef.current && !room.historyMode) {
      wasHistoryRef.current = false;
      lastMessageIdRef.current = chatMessages[chatMessages.length - 1]?.id || '';
      setNewMessageCount(0);
      markLatestRead();
      return;
    }
    wasHistoryRef.current = room.historyMode;
    const latest = chatMessages[chatMessages.length - 1];
    if (!latest) return;
    const previousId = lastMessageIdRef.current;
    if (!previousId) {
      lastMessageIdRef.current = latest.id;
      return;
    }
    if (room.historyMode || previousId === latest.id) return;

    const node = scrollRef.current;
    const shouldFollow = Boolean(node && nearBottom(node));
    const previousIndex = chatMessages.findIndex((message) => message.id === previousId);
    const added = (previousIndex >= 0 ? chatMessages.slice(previousIndex + 1) : [latest])
      .filter((message) => message.type !== 'attack_order' && message.authorUserId !== userId).length;
    lastMessageIdRef.current = latest.id;

    if (shouldFollow) scrollToLatest('smooth');
    else if (added) setNewMessageCount((count) => count + added);
  }, [chatMessages, userId, room.historyMode]);

  const handleScroll = () => {
    onHistoryScroll();
    if (room.historyMode) return;
    const node = scrollRef.current;
    if (!node || !nearBottom(node)) return;
    setNewMessageCount(0);
    markLatestRead();
  };

  return <section
    ref={rootRef}
    className={`doro-mobile-chat${keyboardOpen ? ' is-keyboard-open' : ''}`}
    aria-label="관제 채팅"
  >
    <div id="raid-control-mobile-notice-panel" className="doro-mobile-chat__header">{noticeExpanded ? <RaidChatHeader key={room.context.season?.id} room={room} fullContent /> : null}</div>
    <RaidChatPinProvider room={room}><div className="doro-mobile-chat__messages" ref={scrollRef} data-history={room.historyMode ? 'true' : undefined} onScroll={handleScroll}>
      <RaidChatHistoryControls room={room} onLoadOlder={loadOlder} />
      {chatEntries.length ? chatEntries.map(({ message, event, authorMember }) => {
        if (message.type === 'attack_order') return null;
        return <div className="doro-mobile-chat__entry" key={message.id}>
          {unread.anchorId === message.id ? <div className="doro-mobile-chat__unread"><span>여기부터 안 읽은 메시지</span><em>{unread.count}개</em></div> : null}
          {event ? <EventMessage
            message={message}
            room={room}
            userId={userId}
            replyMessage={messageById.get(message.replyToId) || null}
            onReply={setReplyTo}
            onJump={jumpToMessage}
          /> : <TextMessage
            message={message}
            members={room.members}
            authorMember={authorMember}
            userId={userId}
            replyMessage={messageById.get(message.replyToId) || null}
            onReply={setReplyTo}
            onJump={jumpToMessage}
          />}
        </div>;
      }) : <div className="doro-mobile-chat__empty"><strong>첫 관제 메시지를 남겨주세요.</strong><p>멘션과 공격 지시는 알림으로 전달됩니다.</p></div>}
    </div></RaidChatPinProvider>

    {newMessageCount ? <button type="button" className="doro-mobile-chat__new" onClick={() => scrollToLatest()}>
      <span>새 메시지 {newMessageCount}개</span><DororaidIcon name="chevronDown" size={16} />
    </button> : null}

    <MessageComposer
      room={room}
      userId={userId}
      replyTo={replyTo}
      onCancelReply={() => setReplyTo(null)}
      onSelfRequestOpen={onSelfRequestOpen}
      onOrderOpen={onOrderOpen}
      onFocusStart={captureKeyboardFollow}
      keyboardOpen={keyboardOpen}
      viewportSettled={viewportSettled}
      settleVersion={settleVersion}
    />
  </section>;
}

import { siteImageUrl } from '../../../shared/domain/siteImages.js';
